import React from 'react';
import { ClipboardList, BookOpen, DollarSign, CalendarCheck } from 'lucide-react';
import { useApp } from '../../context/AppContext';
import { useAuth } from '../../context/AuthContext';
import StatCard from '../../components/common/StatCard';
import { formatDate } from '../../utils/helpers';
function StudentDashboard() {
  const { students, classes, subjects, timetable, homework, exams, marks, feePayments, feeStructures, attendance, announcements, bookIssues, academicYear } = useApp();
  const { currentUser } = useAuth();
  const student = students.find(s=>s.id===currentUser?.profileId);
  const myClass = classes.find(c=>c.id===student?.classId);
  const myAttendance = attendance.filter(a=>a.entityType==='student'&&a.entityId===student?.id);
  const present = myAttendance.filter(a=>a.status==='present').length;
  const attPct = myAttendance.length>0?Math.round(present/myAttendance.length*100):0;
  const myFeeStructure = feeStructures.find(f=>f.classId===student?.classId);
  const myPaid = feePayments.filter(p=>p.studentId===student?.id).reduce((s,p)=>s+Number(p.amount||0),0);
  const totalDue = myFeeStructure ? Number(myFeeStructure.totalAmount||0) + Number(student?.previousYearBalance||0) : 0;
  const feePending = myFeeStructure ? Math.max(0, totalDue - myPaid) : 0;
  const dayNames = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];
  const todayName = dayNames[new Date().getDay()];
  const myTimetable = timetable.filter(t=>t.classId===student?.classId&&t.day===todayName).sort((a,b)=>a.period-b.period);
  const classHomework = homework.filter(h=>h.classId===student?.classId&&h.status==='active');
  const completedExam = exams.find(e=>e.status==='completed');
  const myMarks = completedExam?marks.filter(m=>m.examId===completedExam.id&&m.studentId===student?.id):[];
  const myBooks = bookIssues.filter(b=>b.borrowerId===student?.id&&!b.returnDate);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Student Dashboard</h1>
        <p className="text-gray-500 text-sm mt-0.5">Academic Year {academicYear} &mdash; Welcome, {student?.firstName}! {myClass&&myClass.name+' - Section '+student?.sectionId}</p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={CalendarCheck} label="Attendance" value={attPct+'%'} subtext={present+'/'+myAttendance.length+' days present'} colorClass={attPct>=75?'bg-green-500':'bg-red-500'} />
        <StatCard icon={ClipboardList} label="Homework" value={classHomework.length} subtext="Active assignments" colorClass="bg-orange-500" />
        <StatCard icon={DollarSign} label="Fee Pending" value={feePending>0?'₹'+feePending.toLocaleString():'Cleared'} subtext={academicYear} colorClass={feePending>0?'bg-red-500':'bg-green-500'} />
        <StatCard icon={BookOpen} label="Books Issued" value={myBooks.length} subtext="From library" colorClass="bg-blue-500" />
      </div>
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
        <h3 className="font-semibold mb-3">Today's Schedule ({todayName})</h3>
        {myTimetable.length>0?(
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
            {myTimetable.map(slot=>{
              const sub = subjects.find(s=>s.id===slot.subjectId);
              return (
                <div key={slot.id} className="bg-purple-50 rounded-lg p-2.5 text-center">
                  <p className="text-purple-400">P{slot.period} • {slot.startTime}</p>
                  <p className="font-semibold text-purple-700 text-sm mt-0.5">{sub?.name||'-'}</p>
                </div>
              );
            })}
          </div>
        ):<p className="text-gray-400 text-sm">No classes today or timetable not set</p>}
      </div>
      {myMarks.length>0&&(
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
          <h3 className="font-semibold mb-3">Recent Results ({completedExam?.name})</h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
            {myMarks.map(m=>{
              const sub = subjects.find(s=>s.id===m.subjectId);
              const pct = Math.round(m.marksObtained/m.maxMarks*100);
              return (
                <div key={m.id} className="bg-gray-50 rounded-lg p-2.5 text-center">
                  <p className="">{sub?.name}</p>
                  <p className="text-xl font-bold">{m.marksObtained}</p>
                  <p className="">/{m.maxMarks} ({pct}%)</p>
                </div>
              );
            })}
          </div>
        </div>
      )}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
          <h3 className="font-semibold mb-3">Active Homework</h3>
          {classHomework.length>0?(
            <div className="space-y-2">
              {classHomework.map(hw=>(
                <div key={hw.id} className="flex items-center justify-between py-2 border-b border-gray-50 last:border-0">
                  <div>
                    <p className="text-sm font-medium">{hw.title}</p>
                    <p className="">{subjects.find(s=>s.id===hw.subjectId)?.name}</p>
                  </div>
                  <span className="text-red-500 font-medium">Due: {formatDate(hw.dueDate)}</span>
                </div>
              ))}
            </div>
          ):<p className="text-gray-400 text-sm">No active homework</p>}
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
          <h3 className="font-semibold mb-3">Announcements</h3>
          <div className="space-y-2">
            {announcements.filter(a=>a.isActive&&(!a.targetRoles||a.targetRoles.includes('student')||a.targetRoles.includes('all'))).slice(0,4).map(ann=>(
              <div key={ann.id} className="py-2 border-b border-gray-50 last:border-0">
                <p className="font-medium text-sm">{ann.title}</p>
                <p className="">{ann.content.slice(0,80)}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
export default StudentDashboard;
